import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { useAuth0 } from '@auth0/auth0-react'

export function createSupabaseClient(supabaseUrl: string, supabaseAnonKey: string) {
  return createClient(supabaseUrl, supabaseAnonKey)
}

// One client per (url, anonKey). Previously every component that called
// useAppSupabase() built its own client via useMemo, and any client that opened
// a realtime channel held its own WebSocket — so a member on the dashboard held
// two connections at once. Sharing a single client multiplexes every channel
// over one WebSocket.
const clientCache = new Map<string, SupabaseClient>()

// Resolves once Auth0 has finished loading, i.e. once it has handled any
// `?code=` in the URL and written the tokens it got for it.
//
// supabase-js calls `accessToken()` from its constructor, to authenticate
// Realtime, and the client is built on the app's very first render. On the
// sign-in callback that is before the code exchange, so the call finds an
// empty cache and goes to the silent-auth iframe. auth0-spa-js then merges
// every later token call into that one in-flight request (`singlePromise`,
// keyed on client, audience and scope), including GymRedirect's call made
// after the tokens had been saved. On iOS the iframe cannot read Auth0's
// cookie, answers `login_required`, and the SDK logs out locally, wiping
// those fresh tokens: a member who had just signed in was signed straight out.
// Chrome's iframe succeeds, which is why this only ever showed on iPhones.
let resolveAuthSettled!: () => void
const authSettled = new Promise<void>((resolve) => {
  resolveAuthSettled = resolve
})

function getSharedClient(
  supabaseUrl: string,
  supabaseAnonKey: string,
  getAccessToken: () => Promise<string>,
): SupabaseClient {
  const key = `${supabaseUrl}|${supabaseAnonKey}`
  let client = clientCache.get(key)
  if (!client) {
    client = createClient(supabaseUrl, supabaseAnonKey, {
      // This one option authenticates *both* halves of the client, and that is
      // the point: REST calls get the bearer header, and the realtime socket is
      // authenticated at construction, re-authenticated on every reconnect and
      // refreshed on every heartbeat (~25s).
      //
      // Never call `supabase.realtime.setAuth(token)` by hand instead. The first
      // explicit token flips realtime-js's `_manuallySetToken` flag permanently,
      // and every automatic refresh is gated on it being false — so one manual
      // call silently kills token refresh for every channel in the app, and the
      // socket goes quiet the moment the Auth0 token expires.
      //
      // getAccessToken is Auth0's getAccessTokenSilently — a stable reference
      // that returns the *current* token, served from its own cache unless the
      // token is near expiry. Returning null (never throwing) when there is no
      // session is what lets public endpoints (gym-info, occupancy) fall back to
      // the publishable key.
      accessToken: async () => {
        await authSettled
        try {
          return (await getAccessToken()) || null
        } catch {
          return null
        }
      },
    })
    clientCache.set(key, client)
  }
  return client
}

export function useAppSupabase(): SupabaseClient {
  return useSupabaseClient(
    import.meta.env.VITE_SUPABASE_URL as string,
    import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  )
}

export function useSupabaseClient(supabaseUrl: string, supabaseAnonKey: string): SupabaseClient {
  const { getAccessTokenSilently, isLoading } = useAuth0()
  // In render, not an effect: effects run child-first, and a child's first
  // token call must not find the gate still shut. Idempotent.
  if (!isLoading) resolveAuthSettled()
  return getSharedClient(supabaseUrl, supabaseAnonKey, getAccessTokenSilently)
}
